import { colorPaletteColorsById } from "@tokenterminal/core/feature/chart/utils/colors"
import { type BadgeProps } from "@tokenterminal/core/ui/messaging/badge/Badge"
export type ColorMode = "categorical" | "trend"

const chartColors = Object.values(colorPaletteColorsById)

const getColoringForChange = (
  decimalChange: number
): {
  direction: "positive" | "negative" | "neutral"
  level?: "high" | "medium" | "low"
} => {
  // Positive changes
  if (decimalChange >= 0.3) {
    return { direction: "positive", level: "high" }
  }
  if (decimalChange >= 0.15) {
    return { direction: "positive", level: "medium" }
  }
  if (decimalChange >= 0.01) {
    return { direction: "positive", level: "low" }
  }

  // Negative changes
  if (decimalChange <= -0.3) {
    return { direction: "negative", level: "high" }
  }
  if (decimalChange <= -0.15) {
    return { direction: "negative", level: "medium" }
  }
  if (decimalChange <= -0.01) {
    return { direction: "negative", level: "low" }
  }

  // No significant change
  return { direction: "neutral" }
}

export const getBadgeColor = (decimalChange: number): BadgeProps["color"] => {
  const coloring = getColoringForChange(decimalChange)
  if (coloring.direction === "positive") {
    return "green"
  } else if (coloring.direction === "negative") {
    return "red"
  }
  return "neutral"
}

export const getTwBadgeTextColor = (decimalChange: number) => {
  const baseColor = getColoringForChange(decimalChange)
  if (baseColor.direction === "positive") {
    return "text-emerald-800 dark:text-emerald-300"
  } else if (baseColor.direction === "negative") {
    return "text-red-800 dark:text-red-300"
  }
  return "text-neutral-800 dark:text-neutral-300"
}

const getColorForChange = (
  decimalChange: number
): { light: string; dark: string } => {
  const coloring = getColoringForChange(decimalChange)

  if (coloring.direction === "positive" && coloring.level === "high") {
    return {
      light: "hsl(var(--green-400))",
      dark: "hsl(var(--green-800))",
    }
  } else if (coloring.direction === "positive" && coloring.level === "medium") {
    return {
      light: "hsl(var(--green-400))",
      dark: "hsl(var(--green-600))",
    }
  } else if (coloring.direction === "positive" && coloring.level === "low") {
    return {
      light: "hsl(var(--green-300))",
      dark: "hsl(var(--green-400))",
    }
  } else if (coloring.direction === "negative" && coloring.level === "high") {
    return {
      light: "hsl(var(--red-400))",
      dark: "hsl(var(--red-800))",
    }
  } else if (coloring.direction === "negative" && coloring.level === "medium") {
    return {
      light: "hsl(var(--red-400))",
      dark: "hsl(var(--red-600))",
    }
  } else if (coloring.direction === "negative" && coloring.level === "low") {
    return {
      light: "hsl(var(--red-300))",
      dark: "hsl(var(--red-400))",
    }
  } else {
    return {
      light: "hsl(var(--gray-400))",
      dark: "hsl(var(--gray-600))",
    }
  }
}

export function getColor({
  colorMode,
  change,
  index,
  theme,
}: {
  colorMode: ColorMode
  change: number
  index: number
  theme: "light" | "dark"
}) {
  if (colorMode === "trend") {
    return getColorForChange(change)[theme]
  }
  return chartColors[index % chartColors.length]
}
